import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { StudioId } from '@/domain/types';
import DashboardKPITile from '@/components/DashboardKPITile';
import InfoTip from '@/components/InfoTip';
import { ReportLineChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import { useFlashHighlight } from '@/hooks/useFlashHighlight';
import { aud } from '@/utils/format';
import {
  funnel,
  kpiTiles,
  cohortSizes,
  closeReasonBreakdown,
  newLeadsByDay,
  depositsPaidByDay,
  completedBookingsByDay,
} from '@/domain/reportingEngine';

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function dayLabel(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00+10:00`);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

interface Props {
  /** The selected date-range window for funnel, KPIs and the trend. */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

/** The default reporting tab: funnel, KPIs, the trend, cohort sizes and close reasons. */
export default function OverviewTab({ days, studioId }: Props) {
  // Subscribe for reactivity; the selectors read a fresh snapshot.
  const clockIso = useSimulationStore((s) => s.clockIso);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const marketingCampaigns = useBrainStore((s) => s.marketingCampaigns);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const pieces = useBrainStore((s) => s.pieces);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);

  const flashing = useFlashHighlight(['stripe-webhook-success', 'booking:checked-out']);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funnelSteps = useMemo(() => funnel(days, studioId), [days, studioId, customers, deposits, bookings, marketingCampaigns]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const kpis = useMemo(() => kpiTiles(days, studioId), [days, studioId, bookings]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cohorts = useMemo(() => cohortSizes(studioId), [studioId, deposits, sequenceState, customers, pieces, rebookFollowupTasks]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const closes = useMemo(() => closeReasonBreakdown(studioId), [studioId, customers, sequenceState]);

  const trend = useMemo(() => {
    const leads = newLeadsByDay(days, studioId);
    const deps = depositsPaidByDay(days, studioId);
    const comp = completedBookingsByDay(days, studioId);
    return days.map((d, i) => ({
      day: dayLabel(d),
      Leads: leads[i]?.count ?? 0,
      Deposits: deps[i]?.count ?? 0,
      Bookings: comp[i]?.count ?? 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, studioId, customers, deposits, bookings]);

  // Mark today only when it falls inside the selected range.
  const todayKey = clockIso.slice(0, 10);
  const todayLabel = days.includes(todayKey) ? dayLabel(todayKey) : undefined;

  const cohortTiles = [
    { key: 'money', label: 'Deposit paid, no date picked', value: cohorts.moneyOnBooks, tone: 'text-gold-bright' },
    { key: 'open', label: 'Open business', value: cohorts.openBusiness, tone: 'text-cream' },
    { key: 'light', label: 'Did not pick up or respond', value: cohorts.lightReds, tone: 'text-cream-soft' },
    { key: 'dark', label: 'Said not interested', value: cohorts.darkReds, tone: 'text-blood' },
  ];

  const cohortInfo: Record<string, string> = {
    money: 'Deposits taken but with no date locked in yet — money on the books awaiting a booking.',
    open: 'Unfinished pieces (in progress or paused) plus open rebook follow-up tasks — work still to close out.',
    light: 'Sequence-red leads we never reached — no answer after the full attempt ladder.',
    dark: 'Sequence-red leads who said no — declined, now on automated outreach.',
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-overview">
      {/* Funnel */}
      <section className="panel p-4">
        <h2 className="heading-serif text-lg text-cream mb-4">
          Funnel, this period
          <InfoTip>
            The acquisition funnel over the selected span: Meta ad spend → inquiries (new leads by first touch) →
            qualified leads (past the initial &apos;new&apos; stage) → deposits paid → bookings completed. Each tile
            shows the step-to-step conversion.
          </InfoTip>
        </h2>
        <div className="grid grid-cols-5 gap-3">
          {funnelSteps.map((step, idx) => {
            const prev = idx === 0 ? null : funnelSteps[idx - 1];
            const rate = prev && prev.value > 0 ? (step.value / prev.value) * 100 : null;
            return (
              <div key={step.stage} className="panel-dark p-4">
                <p className="label-small-caps">{step.stage}</p>
                <p className="font-cinzel text-3xl text-cream mt-2">
                  {step.value.toLocaleString('en-AU')}
                </p>
                {step.kind === 'spend' ? (
                  <p className="font-inter text-[10px] text-cream-soft mt-2 uppercase tracking-widest">
                    {aud(step.value)} spend
                  </p>
                ) : rate !== null ? (
                  <p className="font-inter text-[10px] text-gold-bright mt-2 uppercase tracking-widest">
                    {rate.toFixed(0)}% conversion
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      {/* KPI tiles */}
      <section className="grid grid-cols-3 gap-3">
        <DashboardKPITile
          label="Average booking value"
          value={aud(kpis.avgBookingValueAud)}
          info="Mean amount paid across completed sessions in the selected span."
        />
        <DashboardKPITile
          label="Retention rate"
          value={pct(kpis.retentionRate)}
          info="Share of customers who have had a completed session and came back for more than one (all-time). A placeholder definition until the client sets theirs."
        />
        <DashboardKPITile
          label="No-show rate"
          value={pct(kpis.noShowRate)}
          accent
          info="No-shows ÷ (completed + no-show) sessions in the selected span."
        />
      </section>

      {/* Week trend - flashes when a deposit/checkout fires under it */}
      <section
        className={clsx('panel p-4', flashing && 'report-flash')}
        data-testid="overview-trend"
      >
        <h2 className="heading-serif text-lg text-cream mb-1">
          Trend
          <InfoTip>
            New leads, deposits paid, and bookings completed per day across the selected date range. The dashed line
            marks today when it falls inside the range.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Leads · deposits paid · bookings completed per day
        </p>
        <ReportLineChart
          data={trend}
          xKey="day"
          series={[
            { key: 'Leads', name: 'Leads', color: REPORT_COLORS.azure },
            { key: 'Deposits', name: 'Deposits paid', color: REPORT_COLORS.gold },
            { key: 'Bookings', name: 'Bookings completed', color: REPORT_COLORS.mint },
          ]}
          references={todayLabel ? [{ x: todayLabel, label: 'Today', dashed: true }] : undefined}
        />
      </section>

      {/* Cohort tile row - the queue's money/open/reds, also flashes */}
      <section
        className={clsx('grid grid-cols-4 gap-3', flashing && 'report-flash')}
        data-testid="overview-cohorts"
      >
        {cohortTiles.map((c) => (
          <div key={c.key} className="panel-dark p-4" data-testid={`cohort-${c.key}`}>
            <p className="label-small-caps flex items-center gap-1">
              {c.label}
              {cohortInfo[c.key] ? <InfoTip>{cohortInfo[c.key]}</InfoTip> : null}
            </p>
            <p className={clsx('font-cinzel text-3xl mt-2', c.tone)}>{c.value}</p>
          </div>
        ))}
      </section>

      {/* Close reasons - won vs unresponsive vs genuine lost, from sequence data */}
      <section className="panel p-4" data-testid="overview-close-reasons">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Close reasons
          <InfoTip>
            Of decided leads: won (lead stage booked or completed), did not pick up or respond (sequence-red
            never-reached), and said not interested (sequence-red said-no). Derived from Prompt A sequence data — no
            new field.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Of decided leads: won, did not pick up or respond, said not interested
        </p>
        {closes.total === 0 ? (
          <p className="font-serif text-cream-soft text-sm">No decided leads yet.</p>
        ) : (
          <>
            <div className="flex h-3 rounded overflow-hidden bg-ink-soft/40" data-testid="close-reasons-bar">
              {closes.wonPct > 0 ? (
                <div className="bg-mint" style={{ width: `${closes.wonPct}%` }} title={`Won: ${closes.wonPct.toFixed(0)}%`} />
              ) : null}
              {closes.lightRedPct > 0 ? (
                <div className="bg-gold-bright" style={{ width: `${closes.lightRedPct}%` }} title={`Did not pick up or respond: ${closes.lightRedPct.toFixed(0)}%`} />
              ) : null}
              {closes.darkRedPct > 0 ? (
                <div className="bg-blood" style={{ width: `${closes.darkRedPct}%` }} title={`Said not interested: ${closes.darkRedPct.toFixed(0)}%`} />
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <CloseTile label="Won" value={closes.won} pct={closes.wonPct} tone="text-mint" />
              <CloseTile label="Did not pick up or respond" value={closes.lightReds} pct={closes.lightRedPct} tone="text-gold-bright" />
              <CloseTile label="Said not interested" value={closes.darkReds} pct={closes.darkRedPct} tone="text-blood" />
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function CloseTile({ label, value, pct, tone }: { label: string; value: number; pct: number; tone: string }) {
  return (
    <div className="panel-dark p-3" data-testid={`close-reason-${label.toLowerCase().replace(/ /g, '-')}`}>
      <p className="label-small-caps">{label}</p>
      <p className={clsx('font-cinzel text-2xl mt-1', tone)}>
        {pct.toFixed(0)}%<span className="text-cream-faint text-base"> · {value}</span>
      </p>
    </div>
  );
}
